import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircleIcon, MailIcon, MapPinIcon } from 'lucide-react';
import { SectionHeading } from './SectionHeading';
type Inquiry = 'Manuscript submission' | 'Rights & permissions' | 'General inquiry';
export function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    type: 'General inquiry' as Inquiry,
    message: ''
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };
  const inputClass = 'w-full bg-transparent border-b border-ink/20 py-3 text-ink placeholder-ink/40 focus:border-gold focus:outline-none transition-colors';
  return <section id="contact" className="w-full bg-parchment py-28" data-id="element-73" data-mp="3f62v9k31q8xl">
      <div className="mx-auto max-w-5xl px-6" data-id="element-74" data-mp="ggkxhxx25b7h">
        <SectionHeading eyebrow="Get in Touch" title="Inquiries & submissions" description="Whether you're a bookseller, a reader, or an author with a manuscript, we'd love to hear from you." data-id="element-75" data-mp="gifdcxth2d4p" />

        <div className="mt-16 grid md:grid-cols-[1fr_1.3fr] gap-14" data-id="element-76" data-mp="3ov7e2uk60ev9">
          <div className="space-y-8" data-id="element-77" data-mp="3owihuudm1mmd">
            <div className="flex items-start gap-4" data-id="element-78" data-mp="3b42ludj4mouy">
              <MailIcon className="text-gold mt-1 shrink-0" size={20} data-id="element-79" data-mp="3b16k0e45rg18" />
              <div data-id="element-80" data-mp="3f3be1dyiqeoe">
                <p className="font-serif text-lg text-ink" data-id="element-81" data-mp="3f2rvpw4yho9u">Email</p>
                <a href="mailto:hello@deleonpublishing.com" className="text-ink/65 hover:text-gold transition-colors" data-id="element-82" data-mp="314bqk9mqs5tt">
                  hello@deleonpublishing.com
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4" data-id="element-83" data-mp="2w5gxmev290vz">
              <MapPinIcon className="text-gold mt-1 shrink-0" size={20} data-id="element-84" data-mp="2w83388ye56bk" />
              <div data-id="element-85" data-mp="qed700ifb329">
                <p className="font-serif text-lg text-ink" data-id="element-86" data-mp="qdtrsrk5qsrx">Office</p>
                <p className="text-ink/65" data-id="element-87" data-mp="udlxmwi62ktl">
                  Deleon Publishing LLC
                  <br data-id="element-88" data-mp="23bu6gjj49uge" />
                  United States
                </p>
              </div>
            </div>
            <p className="text-ink/55 text-sm leading-relaxed border-t border-ink/10 pt-6" data-id="element-89" data-mp="1ybfvotq4s6eo">
              We review manuscript submissions on a rolling basis. Please include a
              synopsis and the first three chapters.
            </p>
          </div>

          {submitted ? <motion.div initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="flex flex-col items-center justify-center text-center bg-paper p-10 shadow-sm" data-id="element-90" data-mp="2m2bnhqul71lx">
              <CheckCircleIcon className="text-gold mb-4" size={44} data-id="element-91" data-mp="2d7nh1xd9zdvj" />
              <h3 className="font-serif text-2xl text-ink" data-id="element-92" data-mp="289vpx5kgfvse">Thank you</h3>
              <p className="text-ink/65 mt-2 max-w-sm" data-id="element-93" data-mp="35qkq6utrapzo">
                Your message has been received. A member of our editorial team will
                be in touch soon.
              </p>
            </motion.div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-94" data-mp="38bsddu9ji991">
              <div className="grid sm:grid-cols-2 gap-6" data-id="element-95" data-mp="3d9237b0ucmxe">
                <div data-id="element-96" data-mp="3dbo9z8fwqyek">
                  <label htmlFor="name" className="sr-only" data-id="element-97" data-mp="3i8oirr456f9d">Name</label>
                  <input id="name" required value={form.name} onChange={e => setForm({
                ...form,
                name: e.target.value
              })} placeholder="Your name" className={inputClass} data-id="element-98" data-mp="3iaremrrz72m0" />
                </div>
                <div data-id="element-99" data-mp="4u0jgabuff87">
                  <label htmlFor="email" className="sr-only" data-id="element-100" data-mp="4tifssmupyq9">Email</label>
                  <input id="email" type="email" required value={form.email} onChange={e => setForm({
                ...form,
                email: e.target.value
              })} placeholder="Email address" className={inputClass} data-id="element-101" data-mp="9slkgbyx62xp" />
                </div>
              </div>
              <div data-id="element-102" data-mp="157fh171bc8w">
                <label htmlFor="type" className="sr-only" data-id="element-103" data-mp="15r0wrvw8iro">Inquiry type</label>
                <select id="type" value={form.type} onChange={e => setForm({
              ...form,
              type: e.target.value as Inquiry
            })} className={`${inputClass} cursor-pointer`} data-id="element-104" data-mp="ewmay7i4p5wu">
                  <option data-id="element-105" data-mp="3ebr3ozx9l2nt">General inquiry</option>
                  <option data-id="element-106" data-mp="3ed2gtqclnmrl">Manuscript submission</option>
                  <option data-id="element-107" data-mp="3sb8wrrhlf22z">Rights &amp; permissions</option>
                </select>
              </div>
              <div data-id="element-108" data-mp="3ncp7bh3sj85v">
                <label htmlFor="message" className="sr-only" data-id="element-109" data-mp="3nc5zwubro263">Message</label>
                <textarea id="message" required rows={4} value={form.message} onChange={e => setForm({
              ...form,
              message: e.target.value
            })} placeholder="Your message" className={`${inputClass} resize-none`} data-id="element-110" data-mp="17l1d3tg5vtqr" />
              </div>
              <button type="submit" className="px-8 py-3.5 bg-ink text-paper tracking-wide hover:bg-gold hover:text-ink transition-colors" data-id="element-111" data-mp="2pgtocsqpn5pr">
                Send message
              </button>
            </form>}
        </div>
      </div>
    </section>;
}